import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Loader2, Copy, Check, Send } from 'lucide-react';

export default function FamilyPrincipalInviteButton({ clientId, clientName }) {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Only Owner/Admin can invite
  const allowedRoles = ['super_admin', 'owner', 'admin', 'org_admin', 'practice_manager'];
  if (!allowedRoles.includes(user?.role)) return null;

  const submit = async () => {
    if (!email || !name) { toast.error('Email and name required'); return; }
    setSubmitting(true);
    try {
      const res = await api.post(token, '/portal/invite-principal', {
        clientId, email, name,
      });
      const magicUrl = `${window.location.origin}/portal/login?magic=${res.data.magicToken}`;
      const inviteUrl = `${window.location.origin}${res.data.inviteUrl}`;
      const final = { ...res.data, magicUrl, inviteUrl };
      setResult(final);
      try { await navigator.clipboard?.writeText(magicUrl); setCopied('magic'); } catch { /* ignore */ }
      toast.success('Family principal invited — magic link copied');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite');
    }
    setSubmitting(false);
  };

  const copy = async (kind) => {
    try {
      await navigator.clipboard.writeText(kind === 'magic' ? result.magicUrl : result.inviteUrl);
      setCopied(kind);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const reset = () => {
    setOpen(false);
    setTimeout(() => {
      setResult(null);
      setEmail('');
      setName('');
      setCopied(false);
    }, 200);
  };

  return (
    <>
      <Button
        variant="outline" size="sm"
        onClick={() => setOpen(true)}
        className="gap-1 text-xs h-8 border-[#A86A3A]/30 text-[#A86A3A] hover:bg-[#FFF8F0]"
        data-testid="invite-principal-btn"
      >
        <UserPlus className="w-3 h-3" /> Invite Family Principal
      </Button>

      <Dialog open={open} onOpenChange={(o) => !o && reset()}>
        <DialogContent className="sm:max-w-md" data-testid="invite-principal-modal">
          <DialogHeader>
            <DialogTitle>Invite Family Principal — {clientName}</DialogTitle>
          </DialogHeader>

          {!result ? (
            <div className="space-y-3 py-2">
              <p className="text-xs text-[#8C8078]">
                Generates a magic link AND an invite token. Principal gets read-only access to net worth, family, key dates & documents for this client.
              </p>
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} data-testid="principal-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="principal-email-input" />
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-xs text-green-800">
                <p className="font-medium mb-1">Principal invited</p>
                <p className="text-[10px] opacity-80">Magic link expires {new Date(result.magicExpiresAt).toLocaleDateString('en-AU')}</p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wide text-[#8C8078] mb-1 font-medium">Magic link (one-tap sign-in)</p>
                <div className="rounded-md border border-[#E6E0D9] bg-stone-50 p-2 flex items-center gap-2">
                  <code className="text-[10px] flex-1 truncate text-[#4A3B32]" data-testid="magic-link-display">{result.magicUrl}</code>
                  <Button variant="ghost" size="sm" onClick={() => copy('magic')} className="h-7" data-testid="magic-copy-btn">
                    {copied === 'magic' ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                  </Button>
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wide text-[#8C8078] mb-1 font-medium">Or invite link (set password)</p>
                <div className="rounded-md border border-[#E6E0D9] bg-stone-50 p-2 flex items-center gap-2">
                  <code className="text-[10px] flex-1 truncate text-[#4A3B32]" data-testid="invite-link-display">{result.inviteUrl}</code>
                  <Button variant="ghost" size="sm" onClick={() => copy('invite')} className="h-7">
                    {copied === 'invite' ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                  </Button>
                </div>
              </div>
              <p className="text-[10px] text-[#8C8078]">
                Email is not connected yet — share these links via your usual secure channel.
              </p>
            </div>
          )}

          <DialogFooter>
            {!result ? (
              <>
                <Button variant="outline" onClick={reset}>Cancel</Button>
                <Button onClick={submit} disabled={submitting} className="bg-[#4A3B32] text-white hover:bg-[#3a2e26] gap-1" data-testid="principal-invite-submit">
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  Send Invite
                </Button>
              </>
            ) : (
              <Button onClick={reset} className="bg-[#4A3B32] text-white hover:bg-[#3a2e26]">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
